// Meetings inbox (Phase 5): incoming requests to accept/decline, outgoing
// requests with status, and confirmed meetings with their video link / .ics.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarPlus,
  Video,
  Check,
  Edit,
  X,
  Clock,
  MapPin,
  Phone,
  Send,
  Briefcase,
  Building2,
  User as UserIcon,
  Search,
  Sparkles,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { forumsAPI, jarvisAPI, meetingsAPI, profilesAPI } from '../api/client';
import AskIfMemberCards, { membersFromAskIfAction } from '../components/AskIfMemberCards';
import { useAuth } from '../context/useAuth';
import { useCalendarConnected } from '../hooks/useCalendarConnected';
import Button from '../components/Button';
import EventCalendarAction, { ConnectCalendarHint } from '../components/EventCalendarAction';
import RequestMeetingModal from '../components/RequestMeetingModal';
import SwipeToDelete from '../components/SwipeToDelete';
import ConfirmModal from '../components/ConfirmModal';
import DirectorySearchFilters, {
  applyDirectoryTagFilters,
  countDirectoryFilters,
  EMPTY_DIRECTORY_FILTERS,
  toProfileSearchFilters,
  type DirectoryFilterState,
} from '../components/DirectorySearchFilters';
import { publicRoleBadgeClass, publicRoleLabel } from '../utils/roles';
import type { ChatMessage, ForumListItem, MeetingRequest, UserListItem, UserProfileListItem } from '../types';

const MODE_LABELS: Record<string, string> = {
  in_person: 'In person',
  phone: 'Phone',
  video: 'Video',
  other: 'Other',
};

function meetingPlace(m: MeetingRequest): string {
  const mode = m.meeting_mode ? MODE_LABELS[m.meeting_mode] || '' : '';
  if (m.location && mode) return `${mode} · ${m.location}`;
  return m.location || mode;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// A confirmed meeting is "past" once its scheduled end has gone by; it then
// moves out of the active list into the collapsed Past section.
function isPast(m: MeetingRequest): boolean {
  if (!m.selected_time) return false;
  const end =
    new Date(m.selected_time).getTime() + (m.duration_minutes || 0) * 60_000;
  return end < Date.now();
}

const STATUS_STYLES: Record<string, string> = {
  proposed: 'bg-gold/10 text-gold',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-gray-100 text-gray-500',
  cancelled: 'bg-gray-100 text-gray-500',
  expired: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<string, string> = {
  proposed: 'Pending',
  accepted: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const GHOST_BTN =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-navy hover:bg-navy/5 rounded-lg transition disabled:opacity-50';

function PlaceIcon({ mode }: { mode?: string | null }) {
  if (mode === 'phone') return <Phone size={14} className="text-gray-400 shrink-0" />;
  if (mode === 'video') return <Video size={14} className="text-gray-400 shrink-0" />;
  return <MapPin size={14} className="text-gray-400 shrink-0" />;
}

function QuietLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
      {children}
    </h2>
  );
}

export default function Meetings() {
  const { user } = useAuth();
  const { connected: calendarConnected, loading: calendarLoading } = useCalendarConnected();
  const [meetings, setMeetings] = useState<MeetingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimes, setSelectedTimes] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MeetingRequest | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [hideTarget, setHideTarget] = useState<MeetingRequest | null>(null);
  const [pickingMember, setPickingMember] = useState(false);
  const [invitee, setInvitee] = useState<{ id: number; full_name: string } | null>(null);
  const [editing, setEditing] = useState<MeetingRequest | null>(null);

  const otherParty = (m: MeetingRequest) =>
    user?.id === m.proposer.id ? m.invitee : m.proposer;

  const load = async () => {
    try {
      const data = await meetingsAPI.list();
      setMeetings(data);
      // Default each incoming request's selection to its first proposed time.
      const defaults: Record<number, string> = {};
      data.forEach((m) => {
        if (m.proposed_times.length > 0) defaults[m.id] = m.proposed_times[0];
      });
      setSelectedTimes((prev) => ({ ...defaults, ...prev }));
    } catch {
      toast.error('Could not load your meetings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = (updated: MeetingRequest) => {
    setMeetings((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  // Conference links are provisioned asynchronously after accept, so poll a few
  // times for the URL to appear and patch it into the list when it does.
  const pollConferenceLink = (id: number) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const updated = await meetingsAPI.refreshConference(id);
        if (updated.conference_url) {
          refresh(updated);
          return;
        }
      } catch {
        // Ignore transient errors; the .ics fallback still covers the meeting.
      }
      if (attempts < 5) setTimeout(tick, 3000);
    };
    setTimeout(tick, 3000);
  };

  const handleAccept = async (m: MeetingRequest) => {
    setBusy(m.id);
    try {
      const updated = await meetingsAPI.accept(m.id, selectedTimes[m.id]);
      refresh(updated);
      toast.success('Meeting confirmed!');
      if (!updated.conference_url) pollConferenceLink(updated.id);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Could not accept the meeting.';
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  const handleDecline = async (m: MeetingRequest) => {
    setBusy(m.id);
    try {
      const updated = await meetingsAPI.decline(m.id);
      refresh(updated);
      toast.success('Request declined.');
    } catch {
      toast.error('Could not decline the request.');
    } finally {
      setBusy(null);
    }
  };

  // Cancelling goes through a modal that asks for an optional reason, which
  // is relayed to the other person in the cancellation notification.
  const openCancelModal = (m: MeetingRequest) => {
    setCancelReason('');
    setCancelTarget(m);
  };

  const handleCancel = async () => {
    const m = cancelTarget;
    if (!m) return;
    setCancelTarget(null);
    setBusy(m.id);
    try {
      const updated = await meetingsAPI.cancel(m.id, cancelReason.trim());
      refresh(updated);
      toast.success('Meeting cancelled.');
    } catch {
      toast.error('Could not cancel the meeting.');
    } finally {
      setBusy(null);
    }
  };

  const handleHide = async () => {
    const m = hideTarget;
    if (!m) return;
    setHideTarget(null);
    setBusy(m.id);
    try {
      await meetingsAPI.hide(m.id);
      setMeetings((prev) => prev.filter((row) => row.id !== m.id));
      toast.success('Removed from your history.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Could not remove that meeting.';
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
      </div>
    );
  }

  const incoming = meetings.filter(
    (m) => m.direction === 'incoming' && m.status === 'proposed',
  );
  const outgoing = meetings.filter(
    (m) => m.direction === 'outgoing' && m.status === 'proposed',
  );
  const accepted = meetings.filter((m) => m.status === 'accepted');
  const confirmed = accepted
    .filter((m) => !isPast(m))
    .sort((a, b) => (a.selected_time || '').localeCompare(b.selected_time || ''));
  const past = meetings
    .filter((m) =>
      ['declined', 'cancelled', 'expired'].includes(m.status) || isPast(m),
    )
    .sort((a, b) => (b.selected_time || b.updated_at || '').localeCompare(a.selected_time || a.updated_at || ''));

  const StatusBadge = ({ status }: { status: string }) => (
    <span
      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
        STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );

  const upcomingCount = incoming.length + outgoing.length + confirmed.length;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy">1:1 Meetings</h1>
            <p className="text-gray-600 mt-1">
              Schedule one-on-one meetings with members
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ConnectCalendarHint
              connected={calendarConnected}
              loading={calendarLoading}
              compact
            />
            <Button icon={CalendarPlus} onClick={() => setPickingMember(true)}>
              Schedule 1:1
            </Button>
          </div>
        </div>

        <div className="flex gap-2 mt-4 border-t border-gray-100 pt-4">
          {([
            { id: 'upcoming' as const, label: 'Upcoming', count: upcomingCount },
            { id: 'past' as const, label: 'Past', count: past.length },
          ]).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition ${
                tab === item.id
                  ? 'bg-navy text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {item.label}
              {item.count > 0 && (
                <span
                  className={`text-xs tabular-nums px-1.5 py-0.5 rounded-full ${
                    tab === item.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'upcoming' && upcomingCount === 0 && (
        <div className="bg-white rounded-2xl shadow-sm px-6 py-14 text-center">
          <div className="w-14 h-14 rounded-full bg-off-white flex items-center justify-center mx-auto">
            <CalendarPlus className="text-gray-400" size={26} />
          </div>
          <h2 className="mt-4 font-semibold text-navy">No 1:1s scheduled</h2>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
            Meet with any member — pick a person, offer a few times, and they
            choose one.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Button icon={CalendarPlus} onClick={() => setPickingMember(true)}>
              Schedule a 1:1
            </Button>
            <Link
              to="/members"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-navy hover:bg-navy/5 rounded-lg transition"
            >
              <Search size={16} />
              Browse the directory
            </Link>
          </div>
        </div>
      )}

      {tab === 'upcoming' && upcomingCount > 0 && (
        <div className="space-y-5">
          {incoming.length > 0 && (
            <section>
              <QuietLabel>Needs a response</QuietLabel>
              <div className="space-y-2">
                {incoming.map((m) => (
                  <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-semibold text-navy">{m.title}</p>
                        <p className="text-sm text-gray-500">
                          from {otherParty(m).full_name} · {m.duration_minutes} min
                        </p>
                      </div>
                      <StatusBadge status={m.status} />
                    </div>
                    {m.note && (
                      <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                        {m.note}
                      </p>
                    )}
                    {meetingPlace(m) && (
                      <p className="text-sm text-gray-600 mt-2 flex items-center gap-1.5">
                        <PlaceIcon mode={m.meeting_mode} />
                        {meetingPlace(m)}
                      </p>
                    )}
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        Choose a time
                      </p>
                      <div className="space-y-1">
                        {m.proposed_times.map((t) => (
                          <label
                            key={t}
                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name={`time-${m.id}`}
                              checked={selectedTimes[m.id] === t}
                              onChange={() =>
                                setSelectedTimes((prev) => ({ ...prev, [m.id]: t }))
                              }
                            />
                            <Clock size={14} className="text-gray-400" />
                            {formatTime(t)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        icon={Check}
                        onClick={() => handleAccept(m)}
                        disabled={busy === m.id}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={X}
                        onClick={() => handleDecline(m)}
                        disabled={busy === m.id}
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {confirmed.length > 0 && (
            <section>
              <QuietLabel>Confirmed</QuietLabel>
              <div className="space-y-2">
                {confirmed.map((m) => {
                  const other = otherParty(m);
                  const isProposer = m.proposer.id === user?.id;
                  return (
                    <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex justify-between items-start gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-navy">{m.title}</p>
                          <p className="text-sm text-gray-500">
                            with {other.full_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusBadge status={m.status} />
                          {m.can_edit && (
                            <button
                              type="button"
                              onClick={() => setEditing(m)}
                              className={GHOST_BTN}
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                          )}
                          {isProposer && (
                            <button
                              type="button"
                              onClick={() => openCancelModal(m)}
                              disabled={busy === m.id}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {m.selected_time && (
                          <span className="inline-flex items-center gap-1.5">
                            <Clock size={14} className="text-gray-400" />
                            {formatTime(m.selected_time)} · {m.duration_minutes} min
                          </span>
                        )}
                        {meetingPlace(m) && (
                          <span className="inline-flex items-center gap-1.5">
                            <PlaceIcon mode={m.meeting_mode} />
                            {meetingPlace(m)}
                          </span>
                        )}
                      </p>
                      <div className="flex flex-wrap items-center gap-1 mt-2">
                        {m.conference_url ? (
                          <a
                            href={m.conference_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={GHOST_BTN}
                          >
                            <Video size={14} />
                            Join
                          </a>
                        ) : m.meeting_mode === 'video' ? (
                          <span className="text-xs text-gray-400 self-center px-2.5">
                            No auto video link
                          </span>
                        ) : null}
                        <EventCalendarAction
                          icsUrl={m.ics_url}
                          links={m.calendar_links}
                          filename={`meeting-${m.id}.ics`}
                          connected={calendarConnected}
                          variant="ghost"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {outgoing.length > 0 && (
            <section>
              <QuietLabel>Waiting</QuietLabel>
              <div className="space-y-2">
                {outgoing.map((m) => (
                  <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-navy">{m.title}</p>
                        <p className="text-sm text-gray-500">
                          waiting on {m.awaiting_user?.full_name || otherParty(m).full_name}
                          {' · '}{m.duration_minutes} min
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={m.status} />
                        {m.can_edit && (
                          <button
                            type="button"
                            onClick={() => setEditing(m)}
                            className={GHOST_BTN}
                          >
                            <Edit size={14} />
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openCancelModal(m)}
                          disabled={busy === m.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    {meetingPlace(m) && (
                      <p className="text-sm text-gray-600 mt-2 flex items-center gap-1.5">
                        <PlaceIcon mode={m.meeting_mode} />
                        {meetingPlace(m)}
                      </p>
                    )}
                    {m.proposed_times.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {m.proposed_times.map((t) => formatTime(t)).join(' · ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {tab === 'past' && (
        <section>
          {past.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm px-6 py-14 text-center">
              <div className="w-14 h-14 rounded-full bg-off-white flex items-center justify-center mx-auto">
                <Clock className="text-gray-400" size={26} />
              </div>
              <h2 className="mt-4 font-semibold text-navy">No past 1:1s</h2>
              <p className="mt-1 text-sm text-gray-500">
                Completed, declined, and cancelled meetings land here.
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-400 mb-2">
                Swipe left to remove from your history. The other person still sees it.
              </p>
              <div className="space-y-2">
                {past.map((m) => {
                  const other = otherParty(m);
                  return (
                    <SwipeToDelete
                      key={m.id}
                      disabled={busy === m.id}
                      onDelete={() => setHideTarget(m)}
                    >
                      <div className="px-4 py-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-navy">{m.title}</p>
                            <p className="text-sm text-gray-500">
                              with {other.full_name}
                            </p>
                            {meetingPlace(m) && (
                              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                                <PlaceIcon mode={m.meeting_mode} />
                                {meetingPlace(m)}
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <StatusBadge status={m.status} />
                            {m.selected_time && (
                              <p className="text-sm text-gray-400 mt-1">
                                {formatTime(m.selected_time)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </SwipeToDelete>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {pickingMember && (
        <MemberPickerModal
          currentUserId={user?.id}
          onClose={() => setPickingMember(false)}
          onSelect={(member) => {
            setPickingMember(false);
            setInvitee(member);
          }}
        />
      )}

      {invitee && (
        <RequestMeetingModal
          inviteeId={invitee.id}
          inviteeName={invitee.full_name}
          onClose={() => setInvitee(null)}
          onCreated={() => {
            setInvitee(null);
            load();
          }}
        />
      )}

      {editing && (
        <RequestMeetingModal
          inviteeId={otherParty(editing).id}
          inviteeName={otherParty(editing).full_name}
          meeting={editing}
          onClose={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(hideTarget)}
        title="Remove from your history?"
        message={`“${hideTarget?.title || ''}” will disappear from your Past list. ${hideTarget ? otherParty(hideTarget).full_name : 'The other person'} will still see it.`}
        confirmLabel="Remove"
        onConfirm={handleHide}
        onCancel={() => setHideTarget(null)}
      />

      {cancelTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-meeting-title"
          onClick={() => setCancelTarget(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 id="cancel-meeting-title" className="text-lg font-semibold text-gray-900 mb-1 text-center">
                {cancelTarget.status === 'accepted' ? 'Cancel this meeting?' : 'Cancel this request?'}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                {cancelTarget.status === 'accepted'
                  ? `${cancelTarget.invitee.full_name} will be notified and the meeting comes off both calendars.`
                  : 'The request will be withdrawn.'}
              </p>
              <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Let them know why, if you'd like…"
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
              />
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100">
              <Button variant="ghost" className="flex-1" onClick={() => setCancelTarget(null)}>
                Keep meeting
              </Button>
              <Button variant="danger" className="flex-1" onClick={handleCancel}>
                Cancel meeting
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberPickerModal({
  currentUserId,
  onClose,
  onSelect,
}: {
  currentUserId?: number;
  onClose: () => void;
  onSelect: (member: { id: number; full_name: string }) => void;
}) {
  const [pickerTab, setPickerTab] = useState<'search' | 'jarvis'>('search');
  const [members, setMembers] = useState<UserProfileListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<DirectoryFilterState>(EMPTY_DIRECTORY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [jarvisMessages, setJarvisMessages] = useState<ChatMessage[]>([]);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const jarvisChatEndRef = useRef<HTMLDivElement>(null);
  const jarvisInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    forumsAPI.getAll().then(setForums).catch(() => setForums([]));
    profilesAPI
      .getAll()
      .then((all) => {
        setIndustries([...new Set(all.map((m) => m.industry).filter(Boolean))].sort());
      })
      .catch(() => setIndustries([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const results = await profilesAPI.search(
          query.trim(),
          toProfileSearchFilters(filters),
        );
        if (!cancelled) {
          setMembers(results.filter((member) => member.id !== currentUserId));
        }
      } catch {
        if (!cancelled) toast.error('Could not load members.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearching(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    query,
    currentUserId,
    filters.state,
    filters.forumId,
    filters.industry,
    filters.userType,
    filters.familyStatus,
    filters.hasChildren,
    filters.minYears,
    filters.maxYears,
  ]);

  useEffect(() => {
    if (pickerTab !== 'jarvis' || jarvisMessages.length > 0) return;
    jarvisAPI
      .getFindMemberGreeting()
      .then(({ greeting }) => setJarvisMessages([{ role: 'assistant', content: greeting }]))
      .catch(() =>
        setJarvisMessages([
          { role: 'assistant', content: 'Hi! Who would you like to meet with?' },
        ]),
      );
  }, [pickerTab, jarvisMessages.length]);

  useEffect(() => {
    jarvisChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jarvisMessages]);

  const handleJarvisSend = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;
    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    setJarvisMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setJarvisLoading(true);
    try {
      const result = await jarvisAPI.findMember(userMessage, jarvisMessages);
      setJarvisMessages((prev) => [...prev, {
        role: 'assistant',
        content: result.response,
        members: membersFromAskIfAction(result.action).filter((member) => member.id !== currentUserId),
      }]);
      setTimeout(() => jarvisInputRef.current?.focus(), 100);
    } catch {
      setJarvisMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'I encountered an issue. Try Search Directory instead.' },
      ]);
    } finally {
      setJarvisLoading(false);
    }
  };

  const visible = applyDirectoryTagFilters(members, filters);
  const hasQuery = Boolean(query.trim() || countDirectoryFilters(filters));

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-meeting-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 pb-3">
          <h3 id="schedule-meeting-title" className="text-lg font-semibold text-navy">
            Schedule a 1:1
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 pointer-coarse:p-2.5 pointer-coarse:-m-1.5 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <p className="px-5 text-sm text-gray-500 mb-3">
          Pick who you want to meet with.
        </p>
        <div className="px-5 mb-3 flex gap-2">
          <button
            type="button"
            onClick={() => setPickerTab('search')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              pickerTab === 'search'
                ? 'bg-navy text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Search size={16} />
            Search Directory
          </button>
          <button
            type="button"
            onClick={() => setPickerTab('jarvis')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              pickerTab === 'jarvis'
                ? 'bg-navy text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Sparkles size={16} />
            Ask IF
          </button>
        </div>
        {pickerTab === 'search' && (
        <div className="px-5 mb-3">
          <DirectorySearchFilters
            searchQuery={query}
            onSearchChange={setQuery}
            isSearching={searching}
            filters={filters}
            onFiltersChange={setFilters}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            showAdvanced={showAdvanced}
            onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
            forums={forums}
            industries={industries}
          />
        </div>
        )}
        {pickerTab === 'search' && (
        <div className="overflow-y-auto px-5 pb-5 space-y-2">
          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading members…</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {hasQuery
                ? 'Try adjusting your search or filters'
                : 'Members will appear here once they join'}
            </p>
          ) : (
            visible.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => onSelect(member)}
                className="w-full bg-off-white/60 hover:bg-off-white rounded-xl p-4 text-left transition"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <UserIcon className="text-navy" size={24} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-navy truncate">{member.full_name}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                      {member.industry && (
                        <span className="flex items-center gap-1">
                          <Briefcase size={14} />
                          {member.industry}
                        </span>
                      )}
                      {(member.location_city || member.location_state) && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {[member.location_city, member.location_state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {member.forum_location && (
                        <span className="flex items-center gap-1">
                          <Building2 size={14} />
                          {member.forum_location.name}
                        </span>
                      )}
                    </div>
                    {member.bio && (
                      <p className="mt-1.5 text-sm text-gray-600 line-clamp-2">{member.bio}</p>
                    )}
                    {member.areas_of_expertise && member.areas_of_expertise.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {member.areas_of_expertise.slice(0, 3).map((exp) => (
                          <span
                            key={exp}
                            className="px-2 py-0.5 bg-white text-navy text-xs rounded-full"
                          >
                            {exp}
                          </span>
                        ))}
                        {member.areas_of_expertise.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                            +{member.areas_of_expertise.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    <span
                      className={`mt-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${publicRoleBadgeClass(member.role, member.snapshot_type)}`}
                    >
                      {publicRoleLabel(member.role, member.snapshot_type)}
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        )}
        {pickerTab === 'jarvis' && (
          <div className="flex flex-col min-h-0 flex-1 px-5 pb-5">
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[28vh]">
              {jarvisMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-navy text-white rounded-br-md'
                        : 'bg-off-white text-gray-800 rounded-bl-md'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles size={14} className="text-gold" />
                        <span className="text-xs font-medium text-gold">Ask IF</span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                    {msg.role === 'assistant' && msg.members && msg.members.length > 0 && (
                      <AskIfMemberCards
                        members={msg.members}
                        onPick={(member) => onSelect(member as UserListItem)}
                        pickLabel="Meet"
                      />
                    )}
                  </div>
                </div>
              ))}
              {jarvisLoading && (
                <div className="flex justify-start">
                  <div className="bg-off-white rounded-2xl px-4 py-3 rounded-bl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin text-gold" />
                      <span className="text-sm text-gray-500">Searching...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={jarvisChatEndRef} />
            </div>
            <div className="flex gap-2 mt-3">
              <input
                ref={jarvisInputRef}
                type="text"
                value={jarvisInput}
                onChange={(e) => setJarvisInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJarvisSend()}
                placeholder="Describe who you're looking for..."
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                disabled={jarvisLoading}
              />
              <button
                type="button"
                onClick={handleJarvisSend}
                disabled={!jarvisInput.trim() || jarvisLoading}
                aria-label="Send message"
                className="px-4 py-2.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
